import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { to_local_yyyy_mm_dd } from '../utils/TimeUtils';

interface ReportData {
  headers: string[];
  rows: any[][];
  summary?: any;
  csv?: string;
}

const ViewReports: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [selectedReport, setSelectedReport] = useState<string>('');

  // Check if user has reports.view permission or is Super Admin
  const hasViewReportsAccess =
    hasPermission('reports.view') ||
    (user && (user.role_id === 1 || user.role_name === 'Super Admin'));

  // Check if user has access and redirect if not
  useEffect(() => {
    if (!authLoading && !permissionsLoading && !hasViewReportsAccess) {
      navigate('/reports');
    }
  }, [authLoading, permissionsLoading, hasViewReportsAccess, navigate]);

  // Don't render if no access
  if (!authLoading && !permissionsLoading && !hasViewReportsAccess) {
    return null;
  }

  // Report generation parameters
  const [inventoryDays, setInventoryDays] = useState(7);
  const [bulk_start_date, set_bulk_start_date] = useState(() => to_local_yyyy_mm_dd());
  const [bulk_end_date, set_bulk_end_date] = useState(() => to_local_yyyy_mm_dd());
  const [reportDate, setReportDate] = useState(() => to_local_yyyy_mm_dd());

  const generateReport = async (reportType: string, params: any = {}) => {
    setLoading(true);
    setError(null);
    setReportData(null);

    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `/api/reports/generate/${reportType}${queryParams ? `?${queryParams}` : ''}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setReportData(data.data);
      } else {
        let err_message = 'Failed to generate report';
        try {
          const errorData = await response.json();
          err_message =
            errorData.message ||
            errorData.error?.message ||
            errorData.errors?.[0]?.msg ||
            err_message;
        } catch {
          err_message = response.statusText || err_message;
        }
        setError(err_message);
      }
    } catch (error) {
      setError('Network error occurred');
      console.error('Error generating report:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    if (!reportData?.csv) return;

    const blob = new Blob([reportData.csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedReport}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const renderReportTable = () => {
    if (!reportData) return null;

    const { headers, rows, summary } = reportData;

    return (
      <div className="card">
        <div className="card-header">
          <h3>Report Results</h3>
          <div className="card-actions">
            <button onClick={downloadCSV} className="btn btn-secondary" disabled={!reportData.csv}>
              📄 Download CSV
            </button>
          </div>
        </div>

        {summary && (
          <div className="report-summary">
            <h4>Summary</h4>
            <div className="summary-grid">
              {Object.entries(summary).map(([key, value]) => (
                <div key={key} className="summary-item">
                  <span className="summary-label">
                    {key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}:
                  </span>
                  <span className="summary-value">{String(value)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                {headers.map((header, index) => (
                  <th key={index}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex}>{String(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">📊 View Reports</h1>
        <p className="page-subtitle">Generate and view various reports for your production data</p>
      </div>

      <div className="reports-section">
        <div className="reports-grid">
          {/* Inventory Reports */}
          <div className="card">
            <div className="card-header">
              <h3>📦 Inventory Reports</h3>
            </div>
            <div className="card-content">
              <div className="form-group">
                <label className="form-label">Expiring Inventory</label>
                <div className="input-group">
                  <input
                    type="number"
                    value={inventoryDays}
                    onChange={(e) => setInventoryDays(parseInt(e.target.value))}
                    min="1"
                    max="30"
                    className="form-control"
                  />
                  <span className="input-group-text">days</span>
                </div>
                <button
                  onClick={() => {
                    setSelectedReport('inventory-expiring');
                    generateReport('inventory-expiring', { days: inventoryDays });
                  }}
                  className="btn btn-primary"
                  disabled={loading}
                >
                  Generate Expiring Report
                </button>
              </div>
            </div>
          </div>

          {/* Production Reports */}
          <div className="card">
            <div className="card-header">
              <h3>🏭 Production Reports</h3>
            </div>
            <div className="card-content">
              <div className="form-group">
                <label className="form-label">Report Date</label>
                <input
                  type="date"
                  value={reportDate}
                  onChange={(e) => setReportDate(e.target.value)}
                  className="form-control"
                />
              </div>

              <div className="report-buttons">
                <button
                  onClick={() => {
                    setSelectedReport('production-completions');
                    generateReport('production-completions', { date: reportDate });
                  }}
                  className="btn btn-primary"
                  disabled={loading}
                >
                  Production Completions
                </button>

                <button
                  onClick={() => {
                    setSelectedReport('production-orders');
                    generateReport('production-orders', { date: reportDate });
                  }}
                  className="btn btn-primary"
                  disabled={loading}
                >
                  Production Orders
                </button>

                <button
                  onClick={() => {
                    setSelectedReport('bulk-usage');
                    generateReport('bulk-usage', { date: reportDate });
                  }}
                  className="btn btn-primary"
                  disabled={loading}
                >
                  Bulk Usage
                </button>

                <button
                  onClick={() => {
                    setSelectedReport('inventory-additions');
                    generateReport('inventory-additions', { date: reportDate });
                  }}
                  className="btn btn-primary"
                  disabled={loading}
                >
                  Inventory Additions
                </button>
              </div>
            </div>
          </div>

          {/* Bulk Demand Forecast */}
          <div className="card">
            <div className="card-header">
              <h3>📈 Bulk Demand Forecast</h3>
            </div>
            <div className="card-content">
              <div className="form-group">
                <p className="form-help" style={{ marginBottom: '12px' }}>
                  Matches the production dashboard <strong>Today</strong> list:{' '}
                  <strong>in progress</strong> orders (always), <strong>pending</strong> with a
                  scheduled production day in the range or <strong>planned production date</strong>{' '}
                  in the range. When there are schedule portions on those days, bulk is split by
                  portion time; otherwise full bulk goes to the order default PL. Both dates default
                  to <strong>today</strong>.
                </p>
                <label className="form-label">Scheduled production date range</label>
                <div className="input-group" style={{ gap: '8px', flexWrap: 'wrap' }}>
                  <input
                    type="date"
                    value={bulk_start_date}
                    onChange={(e) => set_bulk_start_date(e.target.value)}
                    className="form-control"
                    aria-label="Start date"
                  />
                  <span style={{ alignSelf: 'center' }}>to</span>
                  <input
                    type="date"
                    value={bulk_end_date}
                    onChange={(e) => set_bulk_end_date(e.target.value)}
                    className="form-control"
                    aria-label="End date"
                  />
                </div>
                <button
                  onClick={() => {
                    setSelectedReport('bulk-demand');
                    generateReport('bulk-demand', {
                      start_date: bulk_start_date,
                      end_date: bulk_end_date,
                    });
                  }}
                  className="btn btn-primary"
                  disabled={loading}
                  style={{ marginTop: '12px' }}
                >
                  Generate Forecast
                </button>
              </div>
            </div>
          </div>
        </div>

        {loading && (
          <div className="loading-overlay">
            <div className="loading-spinner">Generating report...</div>
          </div>
        )}

        {error && <div className="alert alert-error">{error}</div>}

        {reportData && renderReportTable()}
      </div>
    </div>
  );
};

export default ViewReports;
